/**
 * 진행도 조회 — 학급 탭: 선택 학급·과제의 학생별 진행률
 *
 * 학생 목록: Assign/{branch_id}/Lesson/{lesson_id}.students
 * 진행률: shared/utils/reportProgress.calculateReportProgress
 */
import { useEffect, useMemo, useState } from "react";
import useFirestore from "shared/hooks/useFirestore";
import useGetBranchId from "shared/hooks/useGetBranchId";
import { report, student } from "shared/types/Homework";
import {
    buildReportProgressContext,
    calculateReportProgressWithContext,
} from "shared/utils/reportProgress";

const useHomeworkStudentView = (selected_lesson: string, selected_report: report) => {
    const { readFirestoreOneDoc, readFirestore } = useFirestore();
    const { getBranchId } = useGetBranchId();
    const [studentList, setStudentList] = useState<student[]>([]);
    const [isOpenLoadingModal, setIsOpenLoadingModal] = useState(false);
    const [loadingProgress, setLoadingProgress] = useState(0);
    const [searchKeyword, setSearchKeyword] = useState<string>("");
    const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
    const [sortBy, setSortBy] = useState<"name" | "progress">("name");

    useEffect(() => {
        if (selected_lesson && selected_report?.release_id) {
            getStudentAndProgress();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selected_lesson, selected_report?.release_id]);

    const getStudentAndProgress = async () => {
        setIsOpenLoadingModal(true);
        setLoadingProgress(0);
        try {
            const branch_id = await getBranchId();
            const student_uid_list = await getStudentUidList(branch_id);

            const totalStudents = student_uid_list.length;
            let processedStudents = 0;

            const studentPromises = student_uid_list.map(async (student_uid: string) => {
                const studentDoc = await readFirestoreOneDoc(`User/Auth/Student`, student_uid);
                if (!studentDoc.exists()) return null;

                const data = studentDoc.data();
                const status = data.status as string | undefined;
                if (status !== "active" && status !== undefined) return null;

                processedStudents++;
                setLoadingProgress((processedStudents / totalStudents) * 50);
                return { name: data.name, uid: studentDoc.id };
            });

            const students = await Promise.all(studentPromises);
            const validStudents = students.filter(
                (student): student is { name: string; uid: string } =>
                    student !== null && student.uid !== ""
            );

            // 과제 설정·지점 권한은 학생마다 동일하므로 한 번만 조회
            const progressContext = await buildReportProgressContext(
                { readFirestoreOneDoc, readFirestore },
                selected_report,
                selected_report.report_setting,
                branch_id
            );

            const progressPromises = validStudents.map(async (student) => {
                const progress = await calculateReportProgressWithContext(
                    readFirestoreOneDoc,
                    selected_report.release_id,
                    student.uid,
                    progressContext
                );
                processedStudents++;
                setLoadingProgress(50 + ((processedStudents - totalStudents) / totalStudents) * 50);
                return { ...student, progress };
            });

            const studentListWithProgress = await Promise.all(progressPromises);
            setStudentList(studentListWithProgress);
        } catch (error) {
            console.error("Error fetching student data:", error);
            setStudentList([]);
        } finally {
            setIsOpenLoadingModal(false);
            setLoadingProgress(0);
        }
    };

    const getStudentUidList = async (branch_id: string) => {
        const student_uid_list = await readFirestoreOneDoc(
            `Assign/${branch_id}/Lesson`,
            selected_lesson
        ).then((res) => {
            if (res.exists()) {
                return res.data().students ?? [];
            }
            return [];
        });
        return student_uid_list;
    };

    const sortStudentList = (list: student[], order: "asc" | "desc", sortField: "name" | "progress") => {
        return [...list].sort((a, b) => {
            if (sortField === "name") {
                const comparison = a.name.localeCompare(b.name, "ko");
                return order === "asc" ? comparison : -comparison;
            }
            const comparison = a.progress - b.progress;
            return order === "asc" ? comparison : -comparison;
        });
    };

    const toggleSortOrder = () => {
        const newOrder = sortOrder === "asc" ? "desc" : "asc";
        setSortOrder(newOrder);
        setStudentList((prev) => sortStudentList(prev, newOrder, sortBy));
    };

    const toggleSortBy = (field: "name" | "progress") => {
        setSortBy(field);
        setStudentList((prev) => sortStudentList(prev, sortOrder, field));
    };

    const handleSearch = (keyword: string) => {
        setSearchKeyword(keyword);
    };

    const filteredStudentList = useMemo(
        () =>
            studentList.filter((student) =>
                student.name.toLowerCase().includes(searchKeyword.toLowerCase())
            ),
        [studentList, searchKeyword]
    );

    return {
        studentList: filteredStudentList,
        isOpenLoadingModal,
        loadingProgress,
        searchKeyword,
        sortOrder,
        sortBy,
        handleSearch,
        toggleSortOrder,
        toggleSortBy,
    };
};

export default useHomeworkStudentView;
